 
/**
 * LifecycleTransitionField
 *
 * Shared component for optionally transitioning a client to a lifecycle stage.
 * Used by Plan, Assessment, and other actions that support lifecycle transitions.
 */

import { useQuery } from '@tanstack/react-query';
import { FC } from 'react';
import { Box } from 'src/components/Box';
import { fetchAllLifecycleStages } from 'src/models/lifecycle/lifecycle';
import { FormSelect } from 'src/shared/Forms/Fields/FormSelect';

export interface LifecycleTransitionFieldProps {
  /** Formik field name for the lifecycle stage ID */
  name: string;
  /** Optional label prefix (default: "and transition to this lifecycle stage") */
  label?: string;
}

export const LifecycleTransitionField: FC<LifecycleTransitionFieldProps> = ({
  name,
  label = 'and transition to this lifecycle stage (optional)',
}) => {
  const { data: lifecycleStages = [], isLoading } = useQuery({
    queryKey: ['lifecycle-stages', 'all'],
    queryFn: fetchAllLifecycleStages,
    staleTime: 60000,
  });

  const options = lifecycleStages.map((stage) => ({
    value: stage.id.toString(),
    label: stage.name,
  }));

  return (
    <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
      <Box>{label}</Box>
      <Box sx={{ minWidth: 200 }}>
        <FormSelect
          name={name}
          options={options}
          prompt={isLoading ? 'Loading...' : 'No Lifecycle Transition'}
        />
      </Box>
    </Box>
  );
};
